import type { TemplateDefinition } from '../../overview/models';
/* Its own rules, so a screen outside the builder that draws it is styled too. */
import '../../overview/styles/templates.css';

interface TemplateStepRailProps {
  definition: TemplateDefinition;
  step: number;
  maxStep: number;
  onSelect: (index: number) => void;
}

/**
 * The builder's left rail.
 *
 * Four states per step: active, done, reachable and unreached. A step is
 * reachable once it has been reached, so the rail cannot be used to skip
 * validation on step 1.
 *
 * A **group heading** renders unnumbered and unselectable, and the numbering
 * continues across it — only the RFQ kind has one, and a heading counted as a
 * step would put every later step's number out by one.
 */
export function TemplateStepRail({ definition, step, maxStep, onSelect }: TemplateStepRailProps) {
  const headings = definition.groupHeadings ?? [];
  const totalSteps = definition.steps.length;
  /* Niraxo_Supplier.html's rail numbers every step, the passed ones too: no tick. */
  const tall = definition.railVariant === 'tall';

  return (
    <nav className={`templates__rail${tall ? ' templates__rail--tall' : ''}`} aria-label="Template steps">
      <ol>
        {definition.steps.map((entry, index) => {
          const heading = headings.find((candidate) => candidate.beforeStepIndex === index);
          const active = index === step;
          const done = index < step;
          const reachable = index <= maxStep;
          const state = active ? 'active' : done ? 'done' : reachable ? 'reachable' : 'unreached';

          const position =
            index === 0 ? 'first' : index === totalSteps - 1 ? 'last' : 'middle';

          return (
            <li key={entry.sectionKey}>
              {heading && (
                <div className="templates__rail-heading-group">
                  <span className="templates__rail-marker-col" aria-hidden="true">
                    <span className="templates__rail-step-line templates__rail-step-line--middle" />
                  </span>
                  <p className="templates__rail-heading">{heading.label}</p>
                </div>
              )}
              <button
                type="button"
                className={`templates__rail-step templates__rail-step--${state}`}
                onClick={() => {
                  onSelect(index);
                }}
                disabled={!reachable}
                aria-current={active ? 'step' : undefined}
              >
                <span className="templates__rail-marker-col" aria-hidden="true">
                  <span className={`templates__rail-step-line templates__rail-step-line--${position}`} />
                  <span className="templates__rail-marker">
                    {done && !tall ? <i className="pi pi-check" /> : index + 1}
                  </span>
                </span>
                <span className="templates__rail-label">{entry.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

